/*
 * ===============================================================================================================
 *                                Copyright 2021, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import React, { useCallback, useEffect, Fragment, useState } from 'react';
import { useStyles } from '../../../shared/PolicyDialog/styles';
import { Typography, Grid, FormHelperText, IconButton, Alert } from '@mui/material';
import { ErrorMessage } from '../../../shared/ErrorMessage/ErrorMessage';
import { FUNCTION, FUNCTIONAL_ENTITY_HELPER_TEXT, WARNING, ALERT_PERMISSIONS } from '../../../shared/PolicyDialog/constants';
import { useSelector, useDispatch } from 'react-redux';
import { selectError } from '../../../store/error/errorSelector';
import { showErrorAction } from '../../../store/error/errorActionCreator';
import { selectAllPermission, selectPermission } from '../../../store/role/PolicyFunctional/permissionSelector';
import { getPermissionAction } from '../../../store/role/PolicyFunctional/permissionActionCreator';
import { getPermissions } from '../../../store/role/PolicyFunctional/FunctionalData';
import { IPermission } from '../../../models/Permission/PermissionContract';
import { SearchableList } from '../../../shared/CustomList';
import { AutocompleteChangeReason, AutocompleteChangeDetails } from '../../../shared/CustomList/types';
import CloseIcon from '@mui/icons-material/Close';
import { DropdownOptionPolicyFilteringFunction } from "../../../models/Roles/RolesContracts";

interface IFunctionalProps {
  getId: Function;
}

const Functional = ({ getId }: IFunctionalProps) => {
  const dispatch = useDispatch();
  const { classes } = useStyles();

  const showError = useSelector(selectError);
  const selectedPermission = useSelector(selectPermission);
  const selectedAllPermission = useSelector(selectAllPermission);

  const alertPermissions = ALERT_PERMISSIONS;
  const [close, setClose] = useState(false);
  const [listOfPermissions, setListOfPermissions] = useState<IPermission[]>(selectedAllPermission || []);

  const loading = listOfPermissions && listOfPermissions.length > 0 ? false : true;
  const showWarning = !close && selectedPermission && alertPermissions.includes(selectedPermission.name) ? true : false;

  const handleChange = (
    _event: React.ChangeEvent<{}>,
    _value: any,
    _reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<any> | undefined
  ) => {
    if (details && details.option) {
      let filterPermission = listOfPermissions.find(
        (p: IPermission) => p.name && p.name.toUpperCase() === details.option.name.toUpperCase()
      );
      dispatch(getPermissionAction(filterPermission));
      getId(filterPermission?.id);
    } else {
      dispatch(getPermissionAction([]));
    }
    if (close) {
      setClose(false)
    }
  };
  const fetchAllPermissions = useCallback(() => {
    dispatch(getPermissionAction([]));
    dispatch(showErrorAction({ message: '' }));
    getPermissions(dispatch);
  }, [dispatch]);

  useEffect(() => {
    fetchAllPermissions();
  }, [fetchAllPermissions]);

  useEffect(() => {
    const copyOfPermissions = [...(selectedAllPermission || [])];
    let permissions = copyOfPermissions.sort((a: IPermission, b: IPermission) =>
      a.displayName.toUpperCase().localeCompare(b.displayName.toUpperCase())
    );
    setListOfPermissions(permissions);
  }, [selectedAllPermission]);

  const action = (
    <IconButton id="icon-button" color="inherit" aria-label="Close Icon Button" onClick={() => setClose(true)}>
      <CloseIcon id="close" fontSize="small" />
    </IconButton>
  );

  const handleDropdownFiltering: DropdownOptionPolicyFilteringFunction = (options, { inputValue }) => {
    return options.filter(
      (option: any) =>
        option.displayName.toLowerCase().includes(inputValue.toLowerCase()) ||
        option.name.toLowerCase().includes(inputValue.toLowerCase())
    );
  };

  return (
    <Grid container>
      <Grid item xs={6} className={classes.formStyle}>
        <Typography variant="body2">Name</Typography>
        <Typography data-testid="permission-name" className={classes.wrap} variant="body1">
          {selectedPermission ? selectedPermission.name : '--'}
        </Typography>
      </Grid>
      <Grid item xs={6} className={classes.formStyle}>
        <Typography variant="body2">Description</Typography>
        <Typography data-testid="permission-description" className={classes.wrap} variant="body1">
          {selectedPermission ? selectedPermission.description : '--'}
        </Typography>
      </Grid>
      <Grid item xs={12} className={classes.formStyle}>
        <Fragment>
          <SearchableList
            classes={classes}
            loading={loading}
            title={FUNCTION}
            handleChange={handleChange}
            options={listOfPermissions}
            value={selectedPermission || null}
            placeholder={'Search Permission'}
            handleDropdownFiltering={handleDropdownFiltering}
          />
          <FormHelperText>{FUNCTIONAL_ENTITY_HELPER_TEXT}</FormHelperText>
          {showError && <ErrorMessage {...showError} />}
          <Grid item xs={12} className={classes.alert}>
            {showWarning &&
              <Alert id="snackbar-alert-component" elevation={2} variant="outlined" severity={'warning'} action={action} >
                {WARNING}
              </Alert>
            } </Grid>
        </Fragment>
      </Grid>
    </Grid>
  );
};

export default Functional;
